import React from "react";
import styled, { css } from "styled-components";
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Icon } from ".";
import { theme } from "../config/theme";

type StyledCheckboxProps = {
  children?: any;
  label?: string;
  variant?: CheckboxVariant;
};

type CheckboxVariant = "question" | "meeting" | "deliverable" | "milestone" | "primary"

const checkboxStyles = {
  primary: css<{checked: boolean; disabled: boolean;}>`
    color: ${(p) =>
      p.checked
        ? theme.color.formElements.background
        : theme.color.formElements.border};
    box-shadow: inset 0 0 0 1px ${theme.color.formElements.border};
    ${(p) =>
      p.checked
        ? css`
            background: ${theme.color.formElements.background};
            border: 1px solid ${theme.color.formElements.background};
            box-shadow: inset 0 0 0 1px ${theme.color.formElements.background};`
        : css`
            background: none;
          `};
    ${(p) =>
      p.checked &&
      p.disabled &&
      css`
        background: ${theme.color.disabled};
        border: 1px solid ${theme.color.disabled};
        box-shadow: inset 0 0 0 1px ${theme.color.disabled};
      `}

  &:hover:enabled {
      box-shadow: ${(p) => 
        p.checked
          ? `inset 0 0 0 1px ${theme.color.formElements.background}` 
          : `inset 0 0 0 1px ${theme.color.formElements.hover}`};
  };

  &:focus:enabled {
    outline: 2px solid ${theme.color.formElements.focus};
    box-shadow: inset 0 0 0 1px ${theme.color.formElements.background};
  };
  `,

  question: css<{checked: boolean; disabled: boolean;}>`
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.question};
    ${(p) => p.checked
      ? css`background:${theme.color.checkbox.question}; 
            border: 1px solid ${theme.color.checkbox.question};` 
      : css`background: none;`};
    ${(p) => p.checked && p.disabled && css`opacity: 0.5;`}
    

    &:hover:enabled {
      box-shadow: ${(p) => p.checked 
      ? `inset 0 0 0 1px ${theme.color.checkbox.questionHover}` 
      : `inset 0 0 0 1px ${theme.color.checkbox.question}`};
  };

  &:focus:enabled {
    outline: 2px solid ${theme.color.checkbox.questionFocus};
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.question};
  };
  `,

  meeting: css<{checked: boolean; disabled: boolean;}>`
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.meeting};
    ${(p) => p.checked 
      ? css`background: ${theme.color.checkbox.meeting}; 
            border: 1px solid ${theme.color.checkbox.meeting};` 
      : css`background: none;`};
    ${(p) => p.checked && p.disabled && css`opacity: 0.5;`}

    &:hover:enabled {
      box-shadow: ${(p) => p.checked
        ? `inset 0 0 0 1px ${theme.color.checkbox.meetingHover}` 
        : `inset 0 0 0 1px ${theme.color.checkbox.meeting}`};
  };

    &:focus:enabled {
    outline: 2px solid ${theme.color.checkbox.meetingFocus};
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.meeting};
  };
  `,

  deliverable: css<{checked: boolean; disabled: boolean;}>`
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.deliverable};
    ${(p) => p.checked 
      ? css`background: ${theme.color.checkbox.deliverable}; 
            border: 1px solid ${theme.color.checkbox.deliverable};` 
      : css`background: none;`};
    ${(p) => p.checked && p.disabled && css`opacity: 0.5;`}

    &:hover:enabled {
      box-shadow: ${(p) => p.checked 
      ? `inset 0 0 0 1px ${theme.color.checkbox.delivHover}`
      : `inset 0 0 0 1px ${theme.color.checkbox.deliverable}`};
  };

    &:focus:enabled {
    outline: 2px solid ${theme.color.checkbox.delivFocus};
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.deliverable};
  };
  `,

  milestone: css<{checked: boolean; disabled: boolean;}>`
  box-shadow: inset 0 0 0 1px ${theme.color.checkbox.milestone};
  ${(p) => p.checked 
    ? css`background: ${theme.color.checkbox.milestone}; 
          border: 1px solid ${theme.color.checkbox.milestone};` 
    : css`background: none;`};
  ${(p) => p.checked && p.disabled && css`opacity: 0.5;`}

    &:hover:enabled {
      box-shadow: ${(p) => p.checked
        ? `inset 0 0 0 1px ${theme.color.checkbox.milestoneHover}` 
        : `inset 0 0 0 1px ${theme.color.checkbox.milestone}`};
  };

    &:focus:enabled {
    outline: 2px solid ${theme.color.checkbox.milestoneFocus};
    box-shadow: inset 0 0 0 1px ${theme.color.checkbox.milestone};
  };          
  `,
}

const CheckboxWrapper = styled.div`
  width: max-content;
  align-items: center;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
`;

const StyledCheckbox = styled(CheckboxPrimitive.Root)<StyledCheckboxProps>`
  all: unset;
  box-sizing: border-box;
  user-select: none;
  cursor: pointer;

  &:before {
    box-sizing: border-box;
  };

  &:after {
    box-sizing: border-box;
  };
  
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 2px;
  align-items: center;
  appearance: none;
  display: inline-flex;
  justify-content: center;
  line-height: 1;
  margin: 0;
  outline: none;
  padding: 0;
  -webkit-tap-highlight-color: rgba(0,0,0,0);
  overflow: hidden;
  ${(props) =>
    props.variant ? checkboxStyles[props.variant] : checkboxStyles["primary"]};

  &:disabled {
    cursor: not-allowed;
  } 
`;

const StyledIndicator = styled(CheckboxPrimitive.Indicator)`
  align-items: center;
  display: flex;
  justify-content: center;
  font-size: ${theme.font.size.small};
  user-select: none;
  width: 1.6rem;
  height: 1.6rem;
`

const Label = styled.label<{
  disabled: boolean;
}>`
  cursor: pointer;
  margin-left: 0.5rem;
  ${(props) => props.disabled && css`color: ${theme.color.disabled};`};
  font-size: ${theme.font.size.small};

  ${(p) =>
    p.disabled &&
    css`
      color: ${theme.color.disabled};
      cursor: not-allowed;
    `};

  &:focus  {
    outline: 2px solid ${theme.color.formElements.focus};
    border: 1px solid ${theme.color.formElements.background};
  }
`; 


type CheckboxPrimitiveProps = React.ComponentProps<typeof StyledCheckbox>;
type CheckboxProps = CheckboxPrimitiveProps & StyledCheckboxProps;

export const Checkbox = React.forwardRef<React.ElementRef<typeof StyledCheckbox>, CheckboxProps>(
  ({ onClick, ...props }, forwardedRef) => (
    <CheckboxWrapper {...props} position={props.position}>
      <StyledCheckbox {...props} onClick={onClick} ref={forwardedRef}>
        <StyledIndicator>
          <Icon color="white" iconId="checkmark"/>
        </StyledIndicator>
      </StyledCheckbox>
      <Label {...props} position={props.position} onClick={onClick}>{props.label}</Label>
    </CheckboxWrapper>
  )
);
